import { useState } from 'react'

import { useLazyQuery } from '@apollo/client'
import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  BulkAssignProjectRoleDocument,
  BulkAssignProjectRoleMutation,
  EmployeeSelectorV2ItemsDocument,
  ProjectManagementProjectRoleRoleEnum,
  ProjectRolesDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import {
  CreateProjectRolesSelector,
  projectRoleInterface,
} from 'modules/project_management/components/wizards/AddProjectWizard/1_RolesSelectionStep/CreateProjectRolesSelector'

export interface ProjectIdWithPermissions {
  id: number
  canAssignOwners: boolean
}

export interface BulkAssignProjectRolesModalProps {
  projectIdWithPermissions: ProjectIdWithPermissions[]
  projectIds: number[]
  onClose: () => void
}

const BulkAssignProjectRolesModal = ({
  projectIdWithPermissions,
  projectIds,
  onClose,
}: BulkAssignProjectRolesModalProps) => {
  const [projectRoles, setProjectRoles] = useState<projectRoleInterface[]>([])
  const [employeeSelection, setEmployeeSelection] = useState<EmployeeSelectorV2MultiSelectState>({
    type: 'manual',
    ids: [],
  })

  const [fetchEmployees] = useLazyQuery(EmployeeSelectorV2ItemsDocument)

  const [bulkAssignProjectRole] = useMutation(BulkAssignProjectRoleDocument, {
    extractorFn: (data: BulkAssignProjectRoleMutation) =>
      data.projectManagementMutations.bulkAssignProjectmanagementProjectRole,
  })

  const onSubmitModal = async () => {
    const ownerIds = projectRoles.flatMap((role) => (role.role === 'owner' ? role.employee.id : []))
    const editorIds = projectRoles.flatMap((role) =>
      role.role === 'editor' ? role.employee.id : []
    )
    const viewerIds = projectRoles.flatMap((role) =>
      role.role === 'viewer' ? role.employee.id : []
    )
    const response = await bulkAssignProjectRole({
      variables: {
        owners: ownerIds,
        editors: editorIds,
        viewers: viewerIds,
        projectIds,
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: ProjectRolesDocument,
          variables: {
            projectIds,
          },
        },
      ],
    })

    const handled = handleResult(response, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('project_management.toasts.assign_roles_multiple_projects_success'),
      })

      track(trackingEvents.projectManagement.bulkActions.roleProjectsAssignment.eventSubmit, {
        owners_assigned_count: ownerIds.length,
        editors_assigned_count: editorIds.length,
        viewers_assigned_count: viewerIds.length,
        projects_assigned_count: projectIds.length,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_saving'),
      })
    }
    onClose()
  }

  const disabledEmployeeIds = projectRoles.flatMap((role) =>
    role.employee ? [role.employee.id] : []
  )

  const disabledSubmitButton = projectRoles.length === 0

  const onUpdateRow = async (employeeId: number, role: ProjectManagementProjectRoleRoleEnum) => {
    const index = projectRoles.findIndex((pr) => pr.employee.id === employeeId)

    if (index !== -1) {
      setProjectRoles((prev) => prev.map((pr, i) => (i === index ? { ...pr, role } : pr)))
    } else {
      // Fetch employee details for new employee
      const result = await fetchEmployees({ variables: { ids: [employeeId] } })
      const employeeName = result.data?.employees?.employeeNamesConnection?.nodes?.[0]

      if (employeeName) {
        setProjectRoles((prev) => [
          ...prev,
          {
            role,
            employee: {
              id: employeeName.id,
              fullName: employeeName.fullName,
              firstName: employeeName.firstName,
              lastName: employeeName.lastName,
              preferredName: employeeName.preferredName,
              access: {
                avatar: employeeName.access?.avatar ?? null,
              },
            },
          },
        ])
      }
    }
  }

  const onDeleteRow = (employeeId: number) => {
    setProjectRoles((prev) => prev.filter((pr) => pr.employee.id !== employeeId))
  }

  const resetEmployeeSelection = () => {
    setEmployeeSelection({ type: 'manual', ids: [] })
  }

  const isProjectOwner = projectIdWithPermissions
    .filter((item) => projectIds.includes(item.id))
    .every((item) => item.canAssignOwners)

  return (
    <Modal
      illustration={'add-user'}
      title={i18n.t('project_management.modals.bulk_assign_roles.title')}
      description={i18n.t('project_management.modals.bulk_assign_roles.description', {
        projects_number: projectIds.length,
      })}
      onClose={onClose}
      onSubmit={onSubmitModal}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={disabledSubmitButton} submit>
            {i18n.t('common.add')}
          </Button>
        </>
      }
    >
      <Box gap="s16">
        <CreateProjectRolesSelector
          onUpdateRow={onUpdateRow}
          onDeleteRow={onDeleteRow}
          projectRoles={projectRoles}
          isProjectOwner={isProjectOwner}
          excludeEmployeeIds={disabledEmployeeIds}
          resetEmployeeSelection={resetEmployeeSelection}
          employeeSelection={employeeSelection}
          onSelectEmployees={setEmployeeSelection}
        />
      </Box>
    </Modal>
  )
}

export default observer(BulkAssignProjectRolesModal)
